
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref
} from "vue";
import EntitySelectorCliente from "../../components/cliente/EntitySelectorCliente.vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { formatDateTime } from "../../modules/utils";
import { PaginationResult, defaultResults } from "../../pagination";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { useRntrcFaturamentoService } from "../../modules/business/rntrc/rntrcFaturamentoBusiness";
import {
  RntrcFaturamentoFilterPayload,
  RntrcFaturamentoItemPayload,
  RntrcFaturamentoResumoPayload,
  STATUS_ITEM_FATURAMENTO_RNTRC,
  TIPOS_SERVICO_RNTRC,
  formatCpfCnpj,
  formatMoeda,
  formatPlaca,
  getServicoVeiculoLabel,
  getStatusItemCor,
  getStatusItemLabel,
  getTipoServicoLabel,
  normalizarPlaca,
  toCompetencia
} from "../../modules/business/rntrc/rntrcTypes";

interface RntrcFaturamentoState {
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
  competencia: Date | null;
  filter: RntrcFaturamentoFilterPayload;
  page: number;
  results: PaginationResult<RntrcFaturamentoItemPayload>;
  resumo: Array<RntrcFaturamentoResumoPayload>;
  buscou: boolean;
}

export default defineComponent({
  setup() {
    const hoje = new Date();
    const state = reactive<RntrcFaturamentoState>({
      stateReady: false,
      loadings: [],
      competencia: new Date(hoje.getFullYear(), hoje.getMonth(), 1),
      filter: {},
      page: 0,
      results: defaultResults(),
      resumo: [],
      buscou: false
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const filtroAtual = (): RntrcFaturamentoFilterPayload => ({
      ...state.filter,
      competencia: toCompetencia(state.competencia),
      placa: normalizarPlaca(state.filter.placa) || null
    });

    const executeSearch = () => {
      const { data, loading, listarItens } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      return listarItens(filtroAtual(), state.page)
        .then(() => {
          state.results =
            data.value && data.value.content ? data.value : defaultResults();
          state.page = state.results.page.number;
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const executeResumo = () => {
      const { data, loading, resumo } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      return resumo(filtroAtual())
        .then(() => {
          state.resumo = Array.isArray(data.value) ? data.value : [];
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const submitSearch = () => {
      if (!state.competencia) {
        return;
      }
      state.page = 0;
      state.buscou = true;
      executeSearch();
      executeResumo();
    };

    const afterPageChange = (page?: number) => {
      if (typeof page !== "undefined") {
        state.page = page;
      }
      executeSearch();
    };

    const downloadXlsx = () => {
      if (!state.competencia) {
        return;
      }
      const { loading, downloadXlsx } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      downloadXlsx(filtroAtual())
        .then(() => removeLoading(loading))
        .catch(() => removeLoading(loading));
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : null;
    };

    const totalGeral = computed(() =>
      state.resumo.map((r) => r.valorTotal ?? 0).reduce((a, b) => a + b, 0)
    );

    const totaisPorStatus = computed(() =>
      STATUS_ITEM_FATURAMENTO_RNTRC.map((s) => {
        const linhas = state.resumo.filter((r) => r.flStatusItem === s.flag);
        return {
          flag: s.flag,
          descricao: s.descricao,
          quantidade: linhas
            .map((r) => r.quantidade ?? 0)
            .reduce((a, b) => a + b, 0),
          valor: linhas.map((r) => r.valorTotal ?? 0).reduce((a, b) => a + b, 0)
        };
      }).filter((t) => t.quantidade > 0)
    );

    onMounted(() => {
      state.stateReady = true;
      submitSearch();
    });

    return {
      amIAllowed,
      stateLoading,
      tiposDisponiveis: TIPOS_SERVICO_RNTRC,
      statusDisponiveis: STATUS_ITEM_FATURAMENTO_RNTRC,
      submitSearch,
      afterPageChange,
      downloadXlsx,
      confirmSelectionCliente,
      totalGeral,
      totaisPorStatus,
      normalizarPlaca,
      formatDateTime,
      formatCpfCnpj,
      formatMoeda,
      formatPlaca,
      getServicoVeiculoLabel,
      getStatusItemCor,
      getStatusItemLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente
  }
});
